import { AppContext, BookingData } from "../../../AppDataProvider/AppDataProvider";
import { Button as BaseButton, LoadingSpinner, Notification } from "../../../common/components";
import { BookingVisibilityType, useCreateBookingsWithSquareInvoiceMutation } from "../../../GeneratedGraphQL";
import { Card, ChargeVerifyBuyerDetails, Payments } from "@square/web-payments-sdk-types";
import { getBookingQuestionInput, getCrmFieldBookingInput, requireBookingDataFields } from "../../../utils";
import { marginBottom, marginTop, media } from "../../../common/styles";
import React, { useContext, useEffect, useState } from "react";
import styled, { useTheme } from "styled-components";
import { ActionType } from "../../../AppDataProvider/actions";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { getSquareAppId } from "./utils";
import { loadSquareScript } from "../../../utils";
import i18n from "../../../i18n";
import { Mixpanel } from "../../../mixpanel";
import { PartRequired } from "../../../utils/types";
import { useTranslation } from "react-i18next";
export type SquarePaymentProps = {
  locationId: string;
  onGoBack: () => void;
  onPayAndBook: (bookingIds: string[]) => void;
  priceToPay: number;
};
export function SquarePayment({
  locationId,
  onGoBack,
  onPayAndBook,
  priceToPay
}: SquarePaymentProps): React.ReactElement {
  const {
    t
  } = useTranslation();
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  const theme = useTheme();
  type PaymentsStepBookingData = PartRequired<BookingData, "createdSource" | "organisation" | "reservedSpaceId" | "service" | "timezone" | "userDetails">;
  requireBookingDataFields<PaymentsStepBookingData>(appData.bookingData, ["createdSource", "organisation", "service", "userDetails", "reservedSpaceId", "timezone"]);
  const {
    createdSource,
    organisation,
    userDetails,
    reservedSpaceId,
    timezone,
    service,
    discountCode,
    tier,
    pricePoints,
    bookingNotes,
    bookingQuestionData
  } = appData.bookingData;
  const {
    crmFieldData
  } = userDetails;
  const selectedBookings = appData.bookingData.selectedBookings;
  const confirmPaymentLabel = service.bookingVisibilityType === BookingVisibilityType.Request ? t("requestAndPay") : t("confirmAndPay");
  const [disablePaymentButton, setDisablePaymentButton] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [card, setCard] = useState<Card>();
  const [payments, setPayments] = useState<Payments>();
  const [error, setError] = useState<string | null>(null);
  const [createBookingsWithSquareInvoice] = useCreateBookingsWithSquareInvoiceMutation({
    errorPolicy: "all"
  });
  useEffect(() => {
    const initSquare = async () => {
      try {
        await loadSquareScript();
        if (!window.Square) throw new Error("Square.js failed to load properly");
        const appId = getSquareAppId();
        const payments = window.Square.payments(appId, locationId);
        setPayments(payments);
        const card = await payments.card();
        if (document.getElementById("card-container")?.childNodes.length === 0) {
          await card.attach("#card-container");
        }
        setCard(card);
        setError(null);
        setLoading(false);
      } catch (e) {
        console.error("Initializing Card failed", e);
        setError(t("square.errors.loadingPaymentFormError"));
        setLoading(false);
        return;
      }
    };
    void initSquare();
    setDisablePaymentButton(false);
  }, [locationId, t]);
  const tokenize = async (paymentMethod: Card): Promise<string> => {
    const {
      token,
      errors,
      status
    } = await paymentMethod.tokenize();
    if (!token || errors) {
      let errorMessage = `Tokenization failed with status: ${status}`;
      if (errors) {
        errorMessage += ` and errors: ${JSON.stringify(errors)}`;
      }
      throw new Error(errorMessage);
    }
    return token;
  };
  const verifyBuyer = async (token: string, payments: Payments) => {
    const verificationDetails: ChargeVerifyBuyerDetails = {
      amount: `${priceToPay}`,
      billingContact: {},
      currencyCode: "GBP",
      intent: "CHARGE"
    };
    const verificationResults = await payments.verifyBuyer(token, verificationDetails);
    if (!verificationResults) throw Error("Unable to verify buyer");
    return verificationResults.token;
  };
  const createPayment = async (token: string, squareBuyerVerificationToken: string) => {
    const bookings = [{
      ...(bookingNotes && {
        bookingNotes
      }),
      bookingQuestions: getBookingQuestionInput({
        bookingQuestionData,
        bookingQuestions: organisation.externalBookingQuestions,
        pickedServiceId: service._id
      }),
      customer: {
        ...userDetails,
        crmFieldData: getCrmFieldBookingInput(crmFieldData)
      },
      organisationId: organisation._id,
      reservedSpaceId: reservedSpaceId,
      timezone: timezone,
      ...(selectedBookings && {
        bookingIds: selectedBookings.map(booking => booking._id)
      }),
      ...(pricePoints && {
        pricePoints: pricePoints.map(pricePoint => {
          return {
            pricePointId: pricePoint.pricePoint._id,
            quantity: pricePoint.quantity
          };
        })
      }),
      ...(tier && {
        tierId: tier._id
      })
    }];

    // Ignore lack of test coverage for locale "cimode" as it will only have "cimode" when in test mode
    /* istanbul ignore next */
    const locale = i18n.language !== "cimode" ? i18n.language : "en-GB";
    const {
      data: createdBookings,
      errors: createBookingsResultErrors
    } = await createBookingsWithSquareInvoice({
      variables: {
        bookings,
        ...(!appData.bookingTool.isGenericBookingTool && {
          bookingToolId: appData.bookingTool._id
        }),
        ...(discountCode && {
          discount: {
            customerEmail: userDetails.email,
            discountCode
          }
        }),
        cardToken: token,
        createdSource,
        locale,
        squareBuyerVerificationToken
      }
    });
    if (!createdBookings) {
      if (createBookingsResultErrors?.some(error => error.extensions?.code === "BAD_USER_INPUT" && error.extensions?.appointeddErrorCode !== "CARD_PAYMENT_ERROR")) {
        appDataDispatch({
          payload: {
            buttonLabel: t("reload"),
            content: t("errors.somethingChanged"),
            icon: faExclamationTriangle,
            onClick: /* istanbul ignore next */() => location.reload(),
            title: t("somethingChanged")
          },
          type: ActionType.SHOW_MODAL_DIALOG
        });
      }
      console.error({
        createBookingsResultErrors
      });
      setError(t("square.errors.confirmBookingError"));
      Mixpanel.track({
        name: "Payment failed",
        props: {
          provider: "SQUARE",
          reason: "Error during invoice payment and booking creation"
        }
      });
      return;
    }
    setProcessing(false);

    // Booking has been paid for & confirmed
    Mixpanel.track({
      name: "Payment successful",
      props: {
        provider: "SQUARE"
      }
    });
    onPayAndBook(createdBookings.createBookingsWithSquareInvoice.bookings.map(booking => booking._id));
  };
  const handlePaymentMethodSubmission = async (paymentMethod: Card, payments: Payments) => {
    setProcessing(true);
    setDisablePaymentButton(true);
    try {
      const token = await tokenize(paymentMethod);
      const verificationToken = await verifyBuyer(token, payments);
      await createPayment(token, verificationToken);
    } catch (e) {
      setProcessing(false);
      setError(t("square.errors.tokenGenerationError"));
      console.error("We have an error with tokenisation", e);
      Mixpanel.track({
        name: "Payment failed",
        props: {
          provider: "SQUARE",
          reason: "Tokenisation failed"
        }
      });
    }
    setDisablePaymentButton(false);
    setProcessing(false);
  };
  return <>
      <div id="card-container" />
      {error && <ErrorNotification variant="warning" role="alert">
          {error}
        </ErrorNotification>}
      {loading && (!card || !payments) && <LoadingSpinner colour={theme.colours.textOnPrimary} />}

      {!loading && card && payments && <ButtonContainer>
          <Button variant="secondary" onClick={onGoBack}>
            {t("back")}
          </Button>
          <Button onClick={() => void handlePaymentMethodSubmission(card, payments)} disabled={disablePaymentButton}>
            {processing ? <LoadingSpinner colour={theme.colours.textOnPrimary} /> : confirmPaymentLabel}
          </Button>
        </ButtonContainer>}
    </>;
}
const ButtonContainer = styled.nav`
  width: 100%;
  display: flex;
  ${marginTop.l}
  justify-content: space-between;

  @media ${media.mobile} {
    flex-direction: column-reverse;
    height: 6rem;
  }
`;
const Button = styled(BaseButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 10rem;

  @media ${media.mobile} {
    min-width: 100%;
  }
`;
const ErrorNotification = styled(Notification)`
  ${marginTop.m}
  ${marginBottom.m}
`;
export default SquarePayment;